import { AuthUserPermission, ResourceDetail } from '@cibo/core'
import { LandThumbnail } from '@cibo/landmanager'
import { Can } from '@cibo/profile'
import { DataGridPro } from '@cibo/ui'
import GridViewIcon from '@mui/icons-material/GridView'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { MenuItem, Paper, Select, Stack, Tooltip, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { map, pipe, prop, sort, uniq, uniqBy } from 'ramda'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InfoNeededCorner } from '../../../../components/TraitTable/InfoNeededCorner'
import { useProgramEngagementContext } from '../../../../context'
import { useProgramModel } from '../../../../queries'
import { DebugDetailResetter } from '../../Tasks/BenchmarkStatus/DebugDetailResetter'
import { TaskEditorTableCell } from './TaskEditorTableCell'

export const TaskEditorTable = ({ task }: any) => {
  const { engagement, fieldModels } = useProgramEngagementContext()
  const programModel = useProgramModel({ programId: engagement.data?.programId })
  const benchmark = programModel.data?.benchmarks?.find(
    ({ benchmark }) => benchmark === task.rule.params?.benchmarkName
  )
  const { t } = useTranslation(['traits', '@cibo/programs/TaskEditorTable'])

  const [selectedField, setSelectedField] = useState(fieldModels.data?.items?.[0] ?? null)

  // Build columns from unique years in benchmark.details
  const years: string[] = pipe(
    map(prop('year')),
    // @ts-ignore typing pipe is nearly impossible
    uniq,
    sort((a: number | undefined, b: number | undefined) => (a ?? 0) - (b ?? 0)),
    map(String)
  )(benchmark?.details ?? []) as string[]

  // Build rows from unique traitIds in benchmark.details
  const rows = pipe(
    // @ts-ignore typing pipe is nearly impossible
    uniqBy(prop('traitId')),
    map((detail: ResourceDetail) => {
      const row: { id: string; traitId: string; [key: string]: any } = {
        id: detail.traitId,
        traitId: detail.traitId,
      }
      // Add values for each year from benchmark.details
      ;(benchmark?.details ?? []).forEach(d => {
        if (d.traitId === detail.traitId) {
          row[String(d.year)] = row[String(d.year)] = selectedField?.findDetail({
            traitId: d.traitId,
            year: d.year,
          })
        }
      })
      return row
    })
  )(benchmark?.details ?? []) as { id: string; traitId: string; [key: string]: any }[]

  const columns = [
    {
      field: 'traitId',
      headerName: 'Past Practices',
      flex: 1.5,
      renderCell: ({ value, ...rest }: GridRenderCellParams) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Typography>{t(value)}</Typography>
            <Tooltip title={t(`${value}_description`, { ns: '@cibo/programs/TaskEditorTable' })}>
              <InfoOutlined color="action" />
            </Tooltip>
          </Stack>
        )
      },
    },
    ...years.map((year: string) => ({
      field: year,
      headerName: year,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <TaskEditorTableCell task={task} selectedField={selectedField} {...params} />
      ),
    })),
  ]

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack>
          <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
            <Stack>
              <Typography variant="h6">Field Practices</Typography>
              <Typography>Enter historical practices for each field</Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <GridViewIcon />
              <Select
                value={selectedField?.id ?? 'allFields'}
                onChange={e => {
                  const fieldId = e.target.value
                  if (fieldId !== 'allFields') {
                    const field = fieldModels.data?.items?.find(f => f.id === fieldId)
                    if (field) {
                      setSelectedField(field)
                    }
                  }
                }}
                size="small"
                sx={{ minWidth: 216 }}
              >
                {fieldModels.data?.items?.map(field => (
                  <MenuItem key={field.id} value={field.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LandThumbnail field={field} width={32} height={32} />
                      <Stack>
                        <Typography>{field.name}</Typography>
                        <Typography variant="caption">
                          {field.acres?.toFixed(1) ?? 0} acres
                        </Typography>
                      </Stack>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </Stack>{' '}
          </Stack>
          <Stack direction="row" alignItems="center" pt={2} justifyContent="flex-end">
            <Box
              sx={{
                width: 16,
                height: 16,
                position: 'relative',
              }}
            >
              <InfoNeededCorner />
            </Box>
            <Typography variant="caption" paddingLeft={0.5}>
              {t('infoNeeded', { ns: '@cibo/landmanager/components' })}
            </Typography>
          </Stack>
          <DataGridPro
            getRowHeight={() => 'auto'}
            rows={rows}
            sx={{
              '.MuiDataGrid-columnHeaders': { fontSize: '16px' },
              '.MuiDataGrid-columnHeaderTitle': { fontWeight: '400' },
              '.MuiDataGrid-row:hover': { backgroundColor: 'rgba(24, 48, 56, 0.03)' },
              '.MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft': {
                backgroundColor: 'unset',
              },
              '.MuiDataGrid-cell--pinnedLeft': {
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                boxShadow: '2px 0px 4px -2px #00000036',
              },
            }}
            columns={columns}
            initialState={{ pinnedColumns: { left: ['traitId'] } }}
          />
        </Stack>
      </Paper>
      <Can useAny={[AuthUserPermission.DEBUG_USER]}>
        <DebugDetailResetter accordion benchmark={benchmark} />
      </Can>
    </Stack>
  )
}
